import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Show, UserButton } from "@clerk/react";
import { Loader2, Clock3, ExternalLink } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAdminApi } from "@/components/admin/useAdminApi";
import { Metrics } from "@/components/admin/Metrics";
import { AppointmentsPanel } from "@/components/admin/AppointmentsPanel";
import { SchedulePanel } from "@/components/admin/SchedulePanel";
import { SettingsPanel } from "@/components/admin/SettingsPanel";
import { StaffPanel } from "@/components/admin/StaffPanel";
import { AiPanel } from "@/components/admin/AiPanel";

const TopBar = ({ me }) => (
  <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <img src="/images/logo.png" alt="Smileton Dental logo" className="h-9 w-9 rounded-full" />
        <div className="min-w-0">
          <p className="font-display text-lg text-teal leading-tight">Smileton Admin</p>
          <p className="text-xs text-ink-muted truncate" data-testid="admin-user-role">{me?.email} · {me?.role === "admin" ? "Doctor (admin)" : me?.role}</p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <Link to="/" className="hidden sm:inline-flex items-center gap-1 text-sm text-teal hover:underline" data-testid="admin-view-site-link">View site <ExternalLink className="h-3.5 w-3.5" /></Link>
        <UserButton />
      </div>
    </div>
  </header>
);

const Pending = ({ me }) => (
  <div className="min-h-[70vh] grid place-items-center p-6 text-center" data-testid="admin-pending-approval">
    <div className="max-w-sm">
      <Clock3 className="h-10 w-10 text-teal mx-auto" />
      <h1 className="mt-4 text-2xl font-semibold">Waiting for approval</h1>
      <p className="mt-2 text-ink-muted text-sm">You're signed in as {me.email}. Dr. Vilayath Mahamadi needs to approve your account before you can see clinic data.</p>
    </div>
  </div>
);

const Dashboard = ({ me }) => {
  const [tick, setTick] = useState(0);
  const isDoctor = me.role === "admin";
  const bump = () => setTick((t) => t + 1);
  const tabs = [["appointments", "Appointments"], ...(isDoctor ? [["schedule", "Schedule"], ["settings", "Fee & reasons"], ["staff", "Staff"]] : []), ["ai", "AI assistant"]];
  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
      <Metrics tick={tick} />
      <Tabs defaultValue="appointments">
        <TabsList className="h-auto flex-wrap justify-start bg-white border border-slate-200 rounded-2xl p-1">
          {tabs.map(([v, l]) => <TabsTrigger key={v} value={v} className="rounded-xl data-[state=active]:bg-teal data-[state=active]:text-white" data-testid={`admin-tab-${v}`}>{l}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="appointments"><AppointmentsPanel onChange={bump} /></TabsContent>
        {isDoctor && <TabsContent value="schedule"><SchedulePanel /></TabsContent>}
        {isDoctor && <TabsContent value="settings"><SettingsPanel /></TabsContent>}
        {isDoctor && <TabsContent value="staff"><StaffPanel me={me} /></TabsContent>}
        <TabsContent value="ai"><AiPanel /></TabsContent>
      </Tabs>
    </main>
  );
};

const AdminGate = () => {
  const call = useAdminApi();
  const [me, setMe] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    call("get", "/me").then(setMe).catch(() => setErr(true));
  }, [call]);
  return (
    <div className="min-h-screen bg-alabaster" data-testid="admin-page">
      <TopBar me={me} />
      {err ? <p className="p-10 text-center text-red-600">Could not load your account. Please refresh.</p>
        : !me ? <div className="py-24 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-teal" /></div>
          : me.role === "pending" ? <Pending me={me} /> : <Dashboard me={me} />}
    </div>
  );
};

export default function AdminPage() {
  return (
    <>
      <Show when="signed-in"><AdminGate /></Show>
      <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
    </>
  );
}
